import React, { useState } from "react";
import { Table, Input, Button, Space, Modal, message } from "antd";
import PropTypes from "prop-types";
import { SearchOutlined, MessageOutlined, DeleteOutlined } from "@ant-design/icons";
import FeatherIcon from "feather-icons-react/build/FeatherIcon";
import UploadModal from "./UploadModal";
import { Popconfirm } from "antd";
import { morning_img_01, pdficon4 } from "./imagepath";
import { extractPatientIdFromUrl } from "../utils/helpers";
import { deleteRequest, post } from "../services/base";
import { CSVLink } from "react-csv";
import { DownloadIcon } from "../../src/components/CustomStyle";

const CustomTable = ({
  dataSource,
  columns,
  rowSelection,
  contexto,
  updateDataSource,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [modalCommentContent, setModalCommentContent] = useState(null);
  const [modalCommentVisible, setModalCommentVisible] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [commentLength, setCommentLength] = useState(0);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isTextAreaEmpty, setIsTextAreaEmpty] = useState(true);

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
  };

  const handleReset = (clearFilters) => {
    clearFilters();
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
          >
            Buscar
          </Button>
          <Button
            onClick={() => handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Resetear
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
    render: (text) => {
      if (dataIndex === "estado") {
        if (Number(text) < 110) {
          return (
            <div>
              {text}
              <FeatherIcon
                icon="info"
                color="green"
                style={{ marginLeft: "1em" }}
              />
            </div>
          );
        } else {
          return <div>{text}</div>;
        }
      }
      return text ? text : "-";
    },
  });

  const modifiedColumns = columns.map((col) =>
    col.searchable
      ? {
          ...col,
          ...getColumnSearchProps(col.dataIndex),
          title: (
            <span style={{ color: "var(--primary-color)" }}>{col.title}</span>
          ),
        }
      : col
  );

  const handleDeleteComment = async (record) => {
    try {
      const formDataToSend = {
        id: record.id,
        procedencia: contexto,
      };
      await deleteRequest(
        "/Documentacion/comentario",
        JSON.stringify(formDataToSend)
      );
      setModalCommentVisible(false);
      setIsTextAreaEmpty(true);
      setCommentText(null);
      updateDataSource();
      message.success("Comentario eliminado.");
    } catch (error) {
      console.error("Error eliminando comentario:", error);
      message.error(
        "Error eliminando comentario. Por favor, inténtelo de nuevo más tarde."
      );
    }
  };
  const showModalComment = (record) => {
    setModalCommentContent(record);
    setModalCommentVisible(true);
  };

  const handleOkComment = async () => {
    setLoading(true);
    try {
      const formDataToSend = {
        id: modalCommentContent.id,
        afiliado: extractPatientIdFromUrl(),
        procedencia: contexto,
        comentario: commentText,
      };
      await post("/Documentacion/comentario", JSON.stringify(formDataToSend));
      setModalCommentVisible(false);
      setIsTextAreaEmpty(true);
      setCommentText(null);
      updateDataSource();
      setLoading(false);
      message.success("Comentario agregado.");
    } catch (error) {
      setLoading(false);
      console.error("Error agregando comentario:", error);
      message.error(
        "Error agregando comentario. Por favor, inténtelo de nuevo más tarde."
      );
    }
  };

  const handleCancelComment = () => {
    setModalCommentVisible(false);
    setIsTextAreaEmpty(true);
  };

  const handleCommentChange = (e) => {
    const value = e.target.value;
    setCommentText(value);
    setCommentLength(value.length);
    setIsTextAreaEmpty(value.trim().length === 0);
  };

  const commentColumn = {
    title: "",
    dataIndex: "comentario",
    key: "comentario",
    render: (_, record) => (
      <>
        <Button
          type="link"
          icon={<MessageOutlined />}
          onClick={() => showModalComment(record)}
        />
        {record.comentario && record.comentario.length > 0 && (
          <span
            className="pulse-without-pulse"
            style={{ marginLeft: "23px", position: "relative", top: "-30px" }}
          ></span>
        )}
      </>
    ),
  };

  const fileColumn = {
    title: "",
    dataIndex: "file",
    key: "file",
    render: (_, record) => (
      <>
        <i
          className="fas fa-paperclip"
          onClick={() => {
            setModalContent(record);
            setModalVisible(true);
          }}
          style={{ cursor: "pointer", color: "green" }}
        ></i>
        {record.documentacion && record.documentacion.length > 0 && (
          <span
            className="pulse-without-pulse"
            style={{ marginLeft: "15px", position: "relative", top: "-25px" }}
          ></span>
        )}
      </>
    ),
  };

  modifiedColumns.push(fileColumn, commentColumn);

  if (!dataSource || dataSource.length === 0) {
    return (
      <div style={{ textAlign: "center", marginTop: "20px" }}>
        <img src={morning_img_01} alt="No data" />
        <p style={{ marginTop: "10px" }}>Este paciente no registra datos.</p>
      </div>
    );
  }

  return (
    <div style={{ overflowX: "auto" }}>
      <Space style={{ marginBottom: 16, marginRight:"15px", float: "right" }}>
        <CSVLink
          data={dataSource.map(({ comentario, documentacion, ...rest }) => rest)} 
          filename={`Datos_${contexto}_${extractPatientIdFromUrl()}.csv`}
          style={{ marginRight: "auto"}}
        >
          <img
            src={pdficon4}
            alt="Descargar datos"
            className="float-end patient-views"
          />
        </CSVLink>
      </Space>
      <Table
        pagination={{
          total: dataSource.length,
          showTotal: (total, range) =>
            `Mostrando pagina ${range[0]} con ${range[1]} de ${total} registros`,
        }}
        columns={modifiedColumns}
        dataSource={dataSource}
        rowSelection={{
          selectedRowKeys,
          onChange: onSelectChange,
          ...rowSelection,
        }}
        rowKey={(record) => record.id}
      />
      <UploadModal
        isOpen={modalVisible}
        onClose={() => setModalVisible(false)}
        record={modalContent}
        contexto={contexto}
        actualizarDataSource={updateDataSource}
      />
      <Modal
        title="Agregar un comentario +"
        visible={modalCommentVisible}
        onOk={handleOkComment}
        onCancel={handleCancelComment}
        centered
        okButtonProps={{ disabled: isTextAreaEmpty, loading: loading }}
      >
        <Input.TextArea
          placeholder="Agregar un comentario"
          value={commentText}
          onChange={handleCommentChange}
          maxLength={255}
          autoSize={{ minRows: 2, maxRows: 6 }}
        />
        <p>Caracteres {commentLength}/255</p>

        <h5 style={{ textAlign: "center" }}>Comentarios:</h5>

        {modalCommentContent &&
          modalCommentContent.comentario &&
          modalCommentContent.comentario.length > 0 &&
          modalCommentContent.comentario.map((comentario, index) => (
            <div
              key={index}
              style={{
                position: "relative",
                marginTop: "8px",
                display: "flex",
                alignItems: "flex-start",
              }}
            >
              <div
                style={{
                  backgroundColor: "#f5f5f5",
                  borderRadius: "4px",
                  padding: "8px",
                  flexGrow: 1,
                  display: "flex",
                  justifyContent: "space-between"
                }}
              >
                <p>{comentario.descripcionComentario}</p>
                <p>{new Date(comentario.fechaActualizacion).toISOString().slice(0, 10)}</p>
              </div>
              <div
                style={{
                  marginLeft: "8px",
                  backgroundColor: "rgb(255, 153, 153)",
                  borderRadius: "4px",
                  padding: "11px",
                  height: "auto",
                }}
              >
                <Popconfirm
                  title="¿Está seguro de que desea eliminar este comentario?"
                  onConfirm={() => handleDeleteComment(comentario)}
                  okText="Sí"
                  cancelText="Cancelar"
                >
                  <Button
                    type="link"
                    icon={<DeleteOutlined style={{ color: "white" }} />}
                    style={{ padding: 0 }}
                  />
                </Popconfirm>
              </div>
            </div>
          ))}
      </Modal>
    </div>
  );
};

CustomTable.propTypes = {
  dataSource: PropTypes.array,
  columns: PropTypes.array.isRequired,
  rowSelection: PropTypes.object,
  updateDataSource: PropTypes.func.isRequired,
};

export default CustomTable;
